import { Link } from "react-router";
import type { StepStatus } from "../../api/pathway";
import { Banner } from "../../components/Banner";
import { Guide } from "../../components/Guide";
import { InstallCard } from "../../components/InstallCard";
import { PushCard } from "../../components/PushCard";

interface HubScreenProps {
  trackLabel: string;
  firstName: string;
  gig: StepStatus[];
  finance: StepStatus[];
  doneCount: number;
  totalCount: number;
  percent: number;
  workEnabled: boolean;
  pathwayComplete: boolean;
  /** Replays the walkthrough (closable, nothing saved). */
  onWalkthrough?: () => void;
}

function StepCard({
  prefix,
  step,
  to,
  note,
}: {
  prefix: string;
  step: StepStatus;
  to: string;
  note?: string;
}) {
  const open = step.state !== "locked";
  // The Gig Board never completes: once open it just reads "Open".
  const status =
    step.state === "done"
      ? "Done"
      : step.state === "active"
        ? step.ongoing
          ? "Open"
          : "Do this"
        : "Locked";
  const badge =
    step.state === "done"
      ? "bg-positive text-white"
      : step.state === "active"
        ? "bg-accent text-on-accent"
        : "bg-surface-high text-text-muted";
  const body = (
    <>
      <span
        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-bold ${badge}`}
      >
        {step.state === "done" ? "✓" : step.step}
      </span>
      <span className="flex-1">
        <span className="block text-[11px] font-bold uppercase tracking-[0.08em] text-text-muted">
          {prefix} · Step {step.step}
          {step.ongoing ? " · ongoing" : ""}
        </span>
        <span className="block text-[15px] font-bold text-text">{step.name}</span>
        <span className="block text-[13px] text-text-muted">
          {step.desc}
          {note ? ` ${note}` : ""}
        </span>
      </span>
      <span className="text-[12px] font-bold text-text-muted">{status}</span>
    </>
  );
  const className = `flex items-center gap-3.5 rounded-[var(--radius-card)] border p-4 ${
    step.state === "active" ? "border-accent bg-surface" : "border-border bg-surface"
  } ${open ? "hover:border-accent" : "opacity-60"}`;

  return open ? (
    <Link
      to={to}
      className={className}
      aria-label={`${prefix} step ${step.step}: ${step.name}, ${status}`}
    >
      {body}
    </Link>
  ) : (
    <div className={className} aria-label={`${prefix} step ${step.step}: ${step.name}, locked`}>
      {body}
    </div>
  );
}

/** The combined hub: gig and finance steps interleaved, with progress and the Work-Enabled banner. */
export function HubScreen(props: HubScreenProps) {
  const { gig, finance } = props;
  return (
    <div>
      <div className="mb-4">
        <div className="text-[12px] font-bold uppercase tracking-[0.08em] text-kicker">
          {props.trackLabel}
          {props.firstName ? ` · ${props.firstName}` : ""}
        </div>
        <h2 className="mt-1 text-[22px] text-accent">Your road to Work-Enabled and financed</h2>
        <div
          className="mt-3 h-2.5 overflow-hidden rounded-full bg-surface-high"
          role="progressbar"
          aria-valuenow={props.percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <i className="block h-full bg-accent" style={{ width: `${props.percent}%` }} />
        </div>
        <div className="mt-1.5 text-[13px] text-text-muted">
          {props.doneCount} of {props.totalCount} steps complete
          {props.pathwayComplete ? " · Pathway complete" : ""}
        </div>
      </div>
      <Guide guideKey="hub" done={gig[0]?.state === "done"} />
      <InstallCard />
      {props.workEnabled ? (
        <Banner tone="good">
          ✓ You're Work-Enabled.{" "}
          <Link to="/launch-plan" className="underline">
            Open your Launch Plan
          </Link>
        </Banner>
      ) : null}
      <PushCard onBoard={false} />
      {props.onWalkthrough ? (
        <p className="my-3 text-[13px]">
          <button type="button" className="text-accent underline" onClick={props.onWalkthrough}>
            Take the 2-minute walkthrough of the pathway
          </button>
        </p>
      ) : null}
      {props.pathwayComplete ? (
        <Banner tone="good">
          ✓ Growth Pathway complete.{" "}
          <Link to="/gig/6" className="underline">
            Open the Gig Board
          </Link>
        </Banner>
      ) : null}
      <div className="flex flex-col gap-2.5">
        {gig.map((step, index) => {
          const fin = finance[index];
          // v2: finance Step 5 completes with gig Step 5, and the handover (Step 6) opens after it.
          const gig5Done = gig[4]?.state === "done";
          const finNote =
            fin && fin.state !== "done" && !gig5Done
              ? fin.step === 5
                ? "(completes after gig Step 5)"
                : fin.step === 6
                  ? "(opens after gig Step 5)"
                  : undefined
              : undefined;
          return (
            <div key={step.step} className="contents">
              <StepCard prefix="GIG" step={step} to={`/gig/${step.step}`} />
              {fin ? (
                <StepCard
                  prefix="FINANCE"
                  step={fin}
                  to={`/fin/${fin.step}`}
                  {...(finNote ? { note: finNote } : {})}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
